/*
 ************************************************************************
 *  © [2015 - 2023] Quintype Technologies India Private Limited
 *  All Rights Reserved.
 *************************************************************************
 */

import * as React from "react";
import { t } from "i18n";
import { startOfDay, endOfDay, subDays } from "date-fns";

import Select from "components/select/select";
import DateRangePicker from "components/date-picker/date-range-picker";
import FieldLabel from "components/field-label/field-label";
import { CurrentFilter, DateRange, Range } from "pages/analytics/state";
import { AnalyticsConfig } from "api/route-data/analytics-route-data";
import { Timestamp } from "api/primitive-types";

import styles from "./date-wrapper.module.css";

function getStartTimestamp(startDate: Date) {
  return startOfDay(startDate).valueOf();
}

function getEndTimestamp(endDate: Date) {
  return endOfDay(endDate).valueOf();
}
interface Props {
  currentFilter: CurrentFilter;
  config: AnalyticsConfig;
  onChange: (item: DateRange) => void;
  dateRangeComparison: string;
  onChangeCustomDate: (key: string, startDate: Timestamp, endDate: Timestamp) => void;
  customRange: Range;
  limitCustomInterval?: boolean;
}

const lowerBoundCustomDate = (limitInterval) => {
  if (!limitInterval) {
    return;
  }
  return subDays(new Date(), 3);
};

const DateWrapper: React.SFC<Props> = ({
  currentFilter,
  config,
  onChange,
  dateRangeComparison,
  onChangeCustomDate,
  customRange,
  limitCustomInterval
}) => {
  const { "start-ts": startTsRange1, "end-ts": endTsRange1 } = customRange["range-1"];

  return (
    <div className={styles["analytics-container__date-wrapper"]} data-test-id="analytics-container-date-wrapper">
      <Select
        label={t("analytics.select_range")}
        options={config.periods}
        value={currentFilter.dateRange}
        onChange={(item: DateRange) => onChange(item)}
        getOptionLabel={(item) => item["display-name"]}
        getOptionValue={(item) => item.name}
        classname={"analytics-select-container"}
      />
      {currentFilter.dateRange.name === "custom" ? (
        <div className={styles["date-range-picker-container"]}>
          <FieldLabel label={t("date-range-picker.value")} />
          <DateRangePicker
            startDate={new Date(startTsRange1)}
            endDate={new Date(endTsRange1)}
            onChange={(startDate, endDate) => {
              onChangeCustomDate("range-1", getStartTimestamp(startDate), getEndTimestamp(endDate));
            }}
            minDate={lowerBoundCustomDate(limitCustomInterval)}
            maxDate={new Date()}
            classname="date-wrapper-date-picker"
          />
        </div>
      ) : (
        <div className={styles["date-comparison-container"]}>
          <FieldLabel label={t("date-range-picker.comparison")} />
          <span>{dateRangeComparison}</span>
        </div>
      )}
    </div>
  );
};

export default DateWrapper;
